/**
 * Global circuit breaker for outbound HTTP requests.
 *
 * What it does
 * ────────────
 * If we observe more than `ERROR_THRESHOLD` failing responses inside
 * `WINDOW_MS`, we OPEN the breaker for `OPEN_DURATION_MS`. While OPEN,
 * every new outbound request short-circuits to a synthetic
 * "CircuitOpenError" before reaching the network. After the timeout,
 * we enter HALF_OPEN and let through a single probe: if it succeeds we
 * close, if it fails we re-open.
 *
 * Why it exists
 * ─────────────
 * During the security audit we observed 1800+ failed CORS preflights
 * in 30 seconds because:
 *   1. A Redux slice's `condition` guard did not handle the `failed`
 *      status → after a rejection, the next dispatch was allowed.
 *   2. The failing response triggered a component re-render.
 *   3. The re-rendered component re-dispatched the same fetch.
 *   4. Goto step 1.
 *
 * We fixed the specific slice, but the same shape of bug can recur
 * anywhere:
 *   - new code path that re-dispatches on state change
 *   - browser-level retry on an idempotent GET that loops with a
 *     visibilitychange handler
 *   - a third-party hook (e.g. useSWR) misconfigured with `refetchOnX`
 *
 * The circuit breaker is the LAST line of defense. It does not
 * understand intent; it only counts errors. After ~50 fast failures
 * the user sees a clear "Connection issue, retrying…" UI instead of
 * the browser tab heating up while DevTools renders 4000 red rows.
 *
 * Tuning
 * ──────
 * ERROR_THRESHOLD must be larger than the worst legitimate burst the
 * app makes at boot. /auth/me + dashboard + GA4 + etc. fan
 * out to ~10-15 parallel requests after login. We set 30 so a single
 * outage doesn't trip the breaker, but a pathological loop (which is
 * 100s/sec) does almost instantly.
 *
 * WINDOW_MS is the sliding window the threshold is evaluated over.
 * Short enough that a recovered service unblocks the user quickly,
 * long enough to outlast a normal SPA boot burst.
 *
 * OPEN_DURATION_MS is the cooling-off period before we attempt a
 * probe. 15s is the classic Netflix Hystrix default — long enough that
 * a flapping backend isn't hammered, short enough that the user
 * doesn't feel stuck.
 *
 * Bypassed for
 * ────────────
 *   - Requests with config.bypassCircuitBreaker = true. Reserved for
 *     the auth flow (login/refresh) so a user cannot get
 *     permanently locked out by an earlier outage.
 *   - 4xx responses other than 408, 425, 429: those are CLIENT errors,
 *     not service health signals. We don't want a buggy form that
 *     keeps submitting invalid data (422) to trip the breaker for
 *     the whole app.
 */

import type { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

type State = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

const ERROR_THRESHOLD = 30;
const WINDOW_MS = 10_000;
const OPEN_DURATION_MS = 15_000;

interface BreakerState {
  state: State;
  failureTimestamps: number[];
  openedAt: number | null;
  halfOpenProbeInFlight: boolean;
}

// Store on globalThis so HMR + Turbopack chunk-splitting don't give us
// multiple breakers that each undercount.
const __BREAKER_KEY = '__refinea_circuit_breaker' as const;
type GlobalWithBreaker = typeof globalThis & {
  [__BREAKER_KEY]?: BreakerState;
};
const _g = globalThis as GlobalWithBreaker;

const breaker: BreakerState =
  _g[__BREAKER_KEY] ??
  (_g[__BREAKER_KEY] = {
    state: 'CLOSED',
    failureTimestamps: [],
    openedAt: null,
    halfOpenProbeInFlight: false,
  });

export class CircuitOpenError extends Error {
  readonly isCircuitOpen = true;
  readonly retryAfterMs: number;
  constructor(retryAfterMs: number) {
    super(
      'Circuit breaker is OPEN — request short-circuited. ' +
        `Try again in ~${Math.ceil(retryAfterMs / 1000)}s.`,
    );
    this.name = 'CircuitOpenError';
    this.retryAfterMs = retryAfterMs;
  }
}

function prune(now: number) {
  const cutoff = now - WINDOW_MS;
  // Use lastIndexOf-style scan: timestamps are pushed in order so we
  // only need to find the first kept index.
  let firstKept = 0;
  while (
    firstKept < breaker.failureTimestamps.length &&
    breaker.failureTimestamps[firstKept] < cutoff
  ) {
    firstKept += 1;
  }
  if (firstKept > 0) {
    breaker.failureTimestamps.splice(0, firstKept);
  }
}

function isFailureStatus(status: number | undefined): boolean {
  // Treat anything that isn't a known status as a transport failure.
  // 408, 425, 429 are server-throttling / retry-please codes that we
  // count as health signals. Pure 4xx user errors do NOT count.
  if (status === undefined) return true; // network / CORS / aborted
  if (status >= 500) return true;
  if (status === 408 || status === 425 || status === 429) return true;
  return false;
}

interface ExtendedAxiosConfig {
  bypassCircuitBreaker?: boolean;
}

function shouldBypass(config: AxiosRequestConfig | undefined): boolean {
  if (!config) return false;
  if ((config as ExtendedAxiosConfig).bypassCircuitBreaker) return true;
  return false;
}

/**
 * Request interceptor side. Throws CircuitOpenError if the breaker is
 * OPEN and the request is not flagged for bypass. Returns the (possibly
 * mutated) config otherwise. Also marks the half-open probe in flight.
 */
export function onRequest(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (shouldBypass(config)) return config;

  const now = Date.now();

  // Try transitioning OPEN → HALF_OPEN if the cooling-off period has
  // elapsed. We do this lazily on request rather than via setTimeout
  // so the breaker state stays in sync with actual traffic.
  if (
    breaker.state === 'OPEN' &&
    breaker.openedAt !== null &&
    now - breaker.openedAt >= OPEN_DURATION_MS
  ) {
    breaker.state = 'HALF_OPEN';
    breaker.halfOpenProbeInFlight = false;
  }

  if (breaker.state === 'OPEN' && breaker.openedAt !== null) {
    const retryAfterMs = OPEN_DURATION_MS - (now - breaker.openedAt);
    throw new CircuitOpenError(Math.max(0, retryAfterMs));
  }

  if (breaker.state === 'HALF_OPEN') {
    if (breaker.halfOpenProbeInFlight) {
      // Another probe is already deciding; reject this one as
      // CircuitOpen rather than letting two concurrent probes race.
      throw new CircuitOpenError(1_000);
    }
    breaker.halfOpenProbeInFlight = true;
  }

  return config;
}

/**
 * Response interceptor side — success path.
 * Marks the breaker healthy. In HALF_OPEN this closes the circuit.
 */
export function onResponse(response: AxiosResponse): AxiosResponse {
  if (shouldBypass(response.config)) return response;

  if (breaker.state === 'HALF_OPEN') {
    breaker.state = 'CLOSED';
    breaker.openedAt = null;
    breaker.halfOpenProbeInFlight = false;
    breaker.failureTimestamps.length = 0;
  }
  return response;
}

/**
 * Response interceptor side — error path.
 * Records the failure (if it counts as a health signal) and possibly
 * trips the breaker.
 */
export function onResponseError(error: AxiosError): never {
  // Don't double-count a CircuitOpenError as a new failure.
  if ((error as unknown as { isCircuitOpen?: boolean }).isCircuitOpen) {
    throw error;
  }

  const cfg = error.config as (InternalAxiosRequestConfig & ExtendedAxiosConfig) | undefined;
  if (shouldBypass(cfg)) {
    throw error;
  }

  if (isFailureStatus(error.response?.status)) {
    const now = Date.now();
    breaker.failureTimestamps.push(now);
    prune(now);

    if (breaker.state === 'HALF_OPEN') {
      // Probe failed → back to OPEN with a fresh timer.
      breaker.state = 'OPEN';
      breaker.openedAt = now;
      breaker.halfOpenProbeInFlight = false;
    } else if (
      breaker.state === 'CLOSED' &&
      breaker.failureTimestamps.length >= ERROR_THRESHOLD
    ) {
      breaker.state = 'OPEN';
      breaker.openedAt = now;
      // Don't clear the failure log: if the breaker re-closes quickly,
      // residual recent failures still count.
    }
  }

  throw error;
}

/**
 * Read-only access for UI components that want to show a "connection
 * issue" toast when the breaker is open.
 */
export function getCircuitState(): {
  state: State;
  retryAfterMs: number;
  recentFailures: number;
} {
  const now = Date.now();
  prune(now);
  const retryAfterMs =
    breaker.state === 'OPEN' && breaker.openedAt !== null
      ? Math.max(0, OPEN_DURATION_MS - (now - breaker.openedAt))
      : 0;
  return {
    state: breaker.state,
    retryAfterMs,
    recentFailures: breaker.failureTimestamps.length,
  };
}

/**
 * Manual reset — for an explicit "try again" UI button or a successful
 * login flow that wants to start clean.
 */
export function resetCircuit(): void {
  breaker.state = 'CLOSED';
  breaker.openedAt = null;
  breaker.halfOpenProbeInFlight = false;
  breaker.failureTimestamps.length = 0;
}
